import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./BlogForm.css";
import RichTextEditor from "./RichTextEditor";
import { toast } from "react-toastify";
import { createBlog, getBlogById, updateBlog } from "../../../api/blogs";
import { BLOG_IMAGE_KEYS, resolveBlogImageSrc } from "../../../utils/blogImages";
import {
    ArrowLeft,
    BookMarked,
    Check,
    Copy,
    FileEdit,
    Globe2,
    Images,
    Keyboard,
    Lock,
    MousePointerClick,
    PenLine,
    Rocket,
    Search,
    Sparkles,
    X,
} from "lucide-react";

function slugify(value) {
    return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
}

const EMPTY_FORM = {
    title: "",
    slug: "",
    intro: "",
    image: "",
    author: "",
    status: "draft",
};

function normalizeQuillHtml(html) {
    if (!html) return html;
    // Quill uses NBSP to preserve in-editor spacing; normalize for storage.
    return String(html).replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ");
}

function CoverImageGalleryModal({ open, imageKeys, selectedKey, onClose, onPick }) {
    const [query, setQuery] = useState("");
    const [hoverKey, setHoverKey] = useState(null);

    useEffect(() => {
        if (!open) {
            setQuery("");
            setHoverKey(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const id = window.setTimeout(() => {
            document.getElementById("abf-cover-search")?.focus();
        }, 80);
        return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const filteredKeys = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return imageKeys;
        return imageKeys.filter((key) => key.toLowerCase().includes(q));
    }, [imageKeys, query]);

    const previewKey = useMemo(() => {
        if (!filteredKeys.length) return null;
        if (hoverKey && filteredKeys.includes(hoverKey)) return hoverKey;
        if (selectedKey && filteredKeys.includes(selectedKey)) return selectedKey;
        return filteredKeys[0];
    }, [filteredKeys, hoverKey, selectedKey]);

    const previewSrc = previewKey ? resolveBlogImageSrc(previewKey) : null;

    const copyFilename = () => {
        if (!previewKey) return;
        navigator.clipboard.writeText(previewKey).then(
            () => toast.success("Filename copied"),
            () => toast.error("Could not copy")
        );
    };

    if (!open) return null;

    const totalImages = imageKeys.length;
    const shownCount = filteredKeys.length;
    const isFiltering = query.trim().length > 0;
    const previewMatchesSelection = Boolean(previewKey && selectedKey === previewKey);
    const previewIsForeignHover = Boolean(
        previewKey && hoverKey && hoverKey === previewKey && hoverKey !== selectedKey
    );

    return createPortal(
        <div className="abf-cover-modal" role="presentation">
            <button
                type="button"
                className="abf-cover-modal__backdrop"
                aria-label="Close gallery"
                onClick={onClose}
            />
            <div
                className="abf-cover-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="abf-cover-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="abf-cover-modal__header-glow" aria-hidden />
                <header className="abf-cover-modal__header">
                    <div className="abf-cover-modal__header-main">
                        <div className="abf-cover-modal__brand" aria-hidden>
                            <span className="abf-cover-modal__brand-icon">
                                <Images size={22} strokeWidth={2} />
                            </span>
                        </div>
                        <div className="abf-cover-modal__header-copy">
                            <div className="abf-cover-modal__title-row">
                                <h2 id="abf-cover-modal-title" className="abf-cover-modal__title">
                                    Cover gallery
                                </h2>
                                <span className="abf-cover-modal__title-sparkle">
                                    <Sparkles size={16} strokeWidth={2} aria-hidden />
                                </span>
                                <span
                                    className="abf-cover-modal__count"
                                    title={
                                        isFiltering
                                            ? `${shownCount} match${shownCount === 1 ? "" : "es"} of ${totalImages} images`
                                            : `${totalImages} image${totalImages === 1 ? "" : "s"} in library`
                                    }
                                >
                                    {isFiltering
                                        ? `${shownCount} / ${totalImages}`
                                        : `${totalImages} ${totalImages === 1 ? "asset" : "assets"}`}
                                </span>
                            </div>
                            <p className="abf-cover-modal__subtitle">
                                Hover to preview full size, click to set as your post cover. Files live in{" "}
                                <code className="abf-cover-modal__code">src/assets/images</code>.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="abf-cover-modal__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </header>

                <div className="abf-cover-modal__toolbar">
                    <div className="abf-cover-modal__search-wrap">
                        <Search className="abf-cover-modal__search-icon" size={18} strokeWidth={2} aria-hidden />
                        <input
                            id="abf-cover-search"
                            type="search"
                            className="abf-cover-modal__search-input"
                            placeholder="Filter by filename…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoComplete="off"
                            aria-label="Filter images by filename"
                        />
                        {query ? (
                            <button
                                type="button"
                                className="abf-cover-modal__search-clear"
                                onClick={() => setQuery("")}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        ) : null}
                    </div>
                </div>

                {filteredKeys.length === 0 ? (
                    <div className="abf-cover-modal__empty" role="status">
                        <div className="abf-cover-modal__empty-visual" aria-hidden>
                            <Search size={36} strokeWidth={1.5} />
                        </div>
                        {totalImages === 0 ? (
                            <p className="abf-cover-modal__empty-text">
                                No images found in the blog asset folder yet.
                            </p>
                        ) : (
                            <>
                                <p className="abf-cover-modal__empty-text">
                                    Nothing matches <strong>“{query.trim()}”</strong>. Try another keyword.
                                </p>
                                <button
                                    type="button"
                                    className="abf-cover-modal__empty-btn"
                                    onClick={() => setQuery("")}
                                >
                                    Reset search
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="abf-cover-modal__body">
                        <aside className="abf-cover-modal__preview" aria-live="polite">
                            <div className="abf-cover-modal__preview-inner">
                                <div className="abf-cover-modal__preview-badges">
                                    {previewIsForeignHover ? (
                                        <span className="abf-cover-modal__badge abf-cover-modal__badge--live">
                                            Live preview
                                        </span>
                                    ) : previewMatchesSelection ? (
                                        <span className="abf-cover-modal__badge abf-cover-modal__badge--ok">
                                            <Check size={12} strokeWidth={3} aria-hidden />
                                            Current selection
                                        </span>
                                    ) : (
                                        <span className="abf-cover-modal__badge">Preview</span>
                                    )}
                                </div>
                                <div className="abf-cover-modal__preview-frame">
                                    <div className="abf-cover-modal__preview-shine" aria-hidden />
                                    {previewSrc ? (
                                        <img
                                            src={previewSrc}
                                            alt=""
                                            className="abf-cover-modal__preview-img"
                                        />
                                    ) : (
                                        <span className="abf-cover-modal__preview-fallback" aria-hidden>
                                            ?
                                        </span>
                                    )}
                                </div>
                                <div className="abf-cover-modal__preview-meta">
                                    <code className="abf-cover-modal__preview-key" title={previewKey || ""}>
                                        {previewKey}
                                    </code>
                                    <button
                                        type="button"
                                        className="abf-cover-modal__copy-btn"
                                        onClick={copyFilename}
                                        aria-label="Copy filename to clipboard"
                                    >
                                        <Copy size={15} strokeWidth={2} aria-hidden />
                                    </button>
                                </div>
                                <p className="abf-cover-modal__preview-hint">
                                    {previewMatchesSelection
                                        ? "This image is already your cover — pick another tile to change."
                                        : "Click the matching tile below to apply."}
                                </p>
                            </div>
                        </aside>

                        <div
                            className="abf-cover-modal__grid-col"
                            onMouseLeave={() => setHoverKey(null)}
                        >
                            <div className="abf-cover-modal__grid">
                                {filteredKeys.map((key) => {
                                    const src = resolveBlogImageSrc(key);
                                    const selected = selectedKey === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className={`abf-cover-modal__tile ${selected ? "is-selected" : ""} ${
                                                hoverKey === key ? "is-hover" : ""
                                            }`}
                                            onClick={() => onPick(key)}
                                            onMouseEnter={() => setHoverKey(key)}
                                            onFocus={() => setHoverKey(key)}
                                            aria-pressed={selected}
                                            aria-label={`Use ${key} as cover`}
                                            title={key}
                                        >
                                            <span className="abf-cover-modal__tile-shine" aria-hidden />
                                            <span className="abf-cover-modal__thumb-wrap">
                                                {selected ? (
                                                    <span className="abf-cover-modal__tile-check">
                                                        <Check size={13} strokeWidth={3} aria-hidden />
                                                    </span>
                                                ) : null}
                                                {src ? (
                                                    <img
                                                        src={src}
                                                        alt=""
                                                        className="abf-cover-modal__thumb"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <span className="abf-cover-modal__thumb-fallback" aria-hidden>
                                                        ?
                                                    </span>
                                                )}
                                                <span className="abf-cover-modal__thumb-scrim" aria-hidden />
                                            </span>
                                            <span className="abf-cover-modal__filename">{key}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <footer className="abf-cover-modal__footer">
                    <span className="abf-cover-modal__footer-item">
                        <MousePointerClick size={15} strokeWidth={2} aria-hidden />
                        Click a tile to set cover
                    </span>
                    <span className="abf-cover-modal__footer-dot" aria-hidden />
                    <span className="abf-cover-modal__footer-item">
                        <Keyboard size={15} strokeWidth={2} aria-hidden />
                        Esc to close
                    </span>
                </footer>
            </div>
        </div>,
        document.body
    );
}

const BlogForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [form, setForm] = useState(EMPTY_FORM);

    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [coverGalleryOpen, setCoverGalleryOpen] = useState(false);

    const closeCoverGallery = useCallback(() => setCoverGalleryOpen(false), []);

    const handlePickCoverImage = useCallback(
        (key) => {
            setForm((prev) => ({ ...prev, image: key }));
            setCoverGalleryOpen(false);
        },
        []
    );

    // Fetch blog data if in edit mode
    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;

        const fetchBlog = async () => {
            setLoading(true);
            setMessage({ type: "", text: "" });
            try {
                const blog = await getBlogById(id);
                if (cancelled) return;
                setForm({
                    title: blog?.title || "",
                    slug: blog?.slug || "",
                    intro: blog?.intro || "",
                    image: blog?.image || "",
                    author: blog?.author || "",
                    status: blog?.status === "draft" ? "draft" : "published",
                });
                setContent(blog?.content || "");
            } catch (err) {
                if (cancelled) return;
                const errorMsg = err.response?.data?.message || "Failed to load blog.";
                setMessage({ type: "error", text: errorMsg });
                toast.error(errorMsg);
            } finally {
                if (cancelled) return;
                setLoading(false);
            }
        };
        fetchBlog();

        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    // Ensure form is fully reset when switching from edit route to create route
    useEffect(() => {
        if (isEdit) return;
        setForm(EMPTY_FORM);
        setContent("");
        setMessage({ type: "", text: "" });
        setLoading(false);
        setSubmitting(false);
    }, [isEdit]);

    // Set first image as default in create mode
    useEffect(() => {
        if (isEdit || loading) return;
        if (!form.image && Array.isArray(BLOG_IMAGE_KEYS) && BLOG_IMAGE_KEYS.length > 0) {
            setForm((prev) => ({ ...prev, image: BLOG_IMAGE_KEYS[0] }));
        }
    }, [isEdit, loading, form.image]);

    const imageOptions = useMemo(() => {
        const keys = Array.isArray(BLOG_IMAGE_KEYS) ? BLOG_IMAGE_KEYS : [];
        // If blog has an image key that no longer exists in assets,
        // keep it selectable so the editor doesn't "lose" the value.
        if (form.image && !keys.includes(form.image)) {
            return [form.image, ...keys];
        }
        return keys;
    }, [form.image]);

    const canSubmit = useMemo(() => {
        if (submitting || loading) return false;
        if (!form.title.trim()) return false;
        if (!form.slug.trim()) return false;
        if (!form.intro.trim()) return false;
        if (!form.image.trim()) return false;
        if (!content.trim()) return false;
        if (imageOptions.length === 0) return false;
        return true;
    }, [submitting, loading, form.title, form.slug, form.intro, form.image, content, imageOptions.length]);

    const completion = useMemo(() => {
        const checks = [
            !!form.title.trim(),
            !!form.slug.trim(),
            !!form.intro.trim(),
            !!form.image.trim(),
            !!form.author.trim(),
            !!content.trim()
        ];
        const done = checks.filter(Boolean).length;
        return Math.round((done / checks.length) * 100);
    }, [form.title, form.slug, form.intro, form.image, form.author, content]);

    const primaryActionLabel = useMemo(() => {
        if (submitting) return "Saving…";
        if (form.status === "draft") return isEdit ? "Update draft" : "Save as draft";
        return isEdit ? "Update live post" : "Publish post";
    }, [submitting, form.status, isEdit]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage({ type: "", text: "" });

        try {
            const contentToSave = normalizeQuillHtml(content);
            if (isEdit) {
                // Edit mode
                await updateBlog(id, { ...form, content: contentToSave });
                setMessage({ type: "success", text: "Blog updated successfully!" });
                toast.success("Blog updated successfully!");
                navigate(`/dashboard/blog/${id}`);
            } else {
                // Add mode
                await createBlog({ ...form, content: contentToSave });
                setMessage({ type: "success", text: "Blog created successfully!" });
                toast.success("Blog created successfully!");
                navigate("/dashboard/blog-list");
            }
        } catch (err) {
            console.error(err.response?.data || err);
            const errorText = err.response?.data?.message || 
                (isEdit ? "Failed to update blog" : "Failed to create blog");
            setMessage({ type: "error", text: errorText });
            toast.error(errorText);
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <section className="abf addblog-container" aria-busy="true" aria-label="Loading editor">
                <div className="abf-skel abf-skel-topbar" />
                <div className="abf-skel abf-skel-header" />
                <div className="abf-skel-layout">
                    <div className="abf-skel abf-skel-card-lg" />
                    <div className="abf-skel abf-skel-card-sm" />
                </div>
            </section>
        );
    }

    return (
        <section className="abf addblog-container">
            <div className="abf-topbar">
                <Link className="abf-back-link" to="/dashboard/blog-list">
                    <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                    Back to list
                </Link>
                <span
                    className={`abf-mode-pill ${isEdit ? "edit" : "create"} ${
                        form.status === "published" ? "published" : ""
                    }`}
                    title={form.status === "draft" ? "Draft — not on public blog" : "Published — visible on /blogs"}
                >
                    {form.status === "published" ? (
                        <Globe2 size={14} strokeWidth={2} className="abf-mode-pill-icon" aria-hidden />
                    ) : (
                        <FileEdit size={14} strokeWidth={2} className="abf-mode-pill-icon" aria-hidden />
                    )}
                    {form.status === "published" ? "Live" : "Draft"}
                </span>
            </div>

            <header className="addblog-header">
                <div className="abf-hero-row">
                    <div className="abf-hero-copy">
                        <p className="abf-page-eyebrow">
                            <PenLine size={15} strokeWidth={2} className="abf-page-eyebrow-icon" aria-hidden />
                            {isEdit ? "Update post" : "New post"}
                        </p>
                        <h1 className="addblog-title">
                            {isEdit ? "Refine your article" : "Write a new article"}
                        </h1>
                        <p className="addblog-subtitle">
                            {isEdit
                                ? "Polish metadata, swap the cover, and keep the body aligned with your brand."
                                : "Set title, slug, and excerpt, pick a cover from the library, then compose the full story."}
                        </p>
                    </div>
                    <div className="abf-progress-wrap" aria-label="Form completion">
                        <div className="abf-progress-head">
                            <span className="abf-progress-label">Completion</span>
                            <strong>{completion}%</strong>
                        </div>
                        <div className="abf-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={completion}>
                            <span className="abf-progress-fill" style={{ width: `${completion}%` }} />
                        </div>
                    </div>
                </div>
            </header>

            {message.text && (
                <div className={`addblog-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="addblog-form" onSubmit={handleSubmit}>
                <div className="abf-layout">
                    <div className="abf-main">
                        <div className="addblog-card addblog-card--basics">
                            <header className="abf-basics-head">
                                <div className="abf-basics-head-icon" aria-hidden>
                                    <BookMarked size={20} strokeWidth={2} />
                                </div>
                                <div className="abf-basics-head-copy">
                                    <p className="abf-basics-eyebrow">Post details</p>
                                    <h2 className="abf-basics-heading">Basics</h2>
                                    <p className="abf-basics-lede">
                                        Headline, URL slug, attribution, and the excerpt readers see before opening the full post.
                                    </p>
                                </div>
                            </header>

                            <div className="abf-basics-grid">
                                <div className="field abf-basics-field abf-basics-field--title">
                                    <label className="label" htmlFor="title">Title</label>
                                    <input
                                        id="title"
                                        type="text"
                                        name="title"
                                        placeholder="e.g. Why Anjouan is trending in iGaming"
                                        value={form.title}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="field abf-basics-field abf-basics-field--author">
                                    <label className="label" htmlFor="author">Author</label>
                                    <input
                                        id="author"
                                        type="text"
                                        name="author"
                                        placeholder="e.g. Offshore Advisory"
                                        required
                                        value={form.author}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="field abf-basics-field abf-basics-field--slug">
                                    <div className="label-row">
                                        <label className="label" htmlFor="slug">Slug</label>
                                        <button
                                            type="button"
                                            className="link-btn"
                                            onClick={() => setForm((p) => ({ ...p, slug: slugify(p.title) }))}
                                            disabled={!form.title.trim() || submitting}
                                            title="Generate slug from title"
                                        >
                                            Generate from title
                                        </button>
                                    </div>
                                    <input
                                        id="slug"
                                        type="text"
                                        name="slug"
                                        placeholder="e.g. anjouan-gaming-license"
                                        value={form.slug}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                    />
                                    <div className="hint abf-basics-url-hint">
                                        <span className="abf-basics-url-prefix">Public URL:</span>
                                        <code className="abf-basics-url-sample">https://offshoreadvisory.pro/blog/{form.slug || "your-slug"}</code>
                                    </div>
                                </div>

                                <div className="field abf-basics-field abf-basics-field--intro">
                                    <label className="label" htmlFor="intro">Intro</label>
                                    <textarea
                                        id="intro"
                                        name="intro"
                                        placeholder="Short summary shown in the blog list and previews…"
                                        value={form.intro}
                                        onChange={handleChange}
                                        rows={4}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <RichTextEditor
                            initialContent={content}
                            onChange={(html) => setContent(html)}
                        />
                    </div>

                    <aside className="abf-side" aria-label="Publishing panel">
                        <div className="abf-side-stack abf-sticky-card">
                        {(() => {
                            const src = resolveBlogImageSrc(form.image);
                            return (
                                <div className="addblog-card addblog-image-preview">
                                    <header className="abf-cover-card-head">
                                        <div className="abf-cover-card-head-icon" aria-hidden>
                                            <Images size={20} strokeWidth={2} />
                                        </div>
                                        <div className="abf-cover-card-head-copy">
                                            <p className="abf-cover-card-eyebrow">Visual</p>
                                            <h3 className="abf-cover-card-title">Cover image</h3>
                                            <p className="abf-cover-card-lede">
                                                Choose a filename from the library or open the gallery for a larger preview.
                                            </p>
                                        </div>
                                    </header>
                                    <div className="abf-cover-card-body">
                                    <div className="field addblog-image-field">
                                        <label className="label" htmlFor="image">Select image</label>
                                        <div className="addblog-image-select">
                                            <select
                                                id="image"
                                                name="image"
                                                value={form.image}
                                                onChange={handleChange}
                                                required
                                                disabled={imageOptions.length === 0}
                                            >
                                                {imageOptions.map((key) => (
                                                    <option key={key} value={key} title={key}>
                                                        {key.length > 28 ? `${key.slice(0, 26)}…` : key}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="addblog-gallery-trigger"
                                                onClick={() => setCoverGalleryOpen(true)}
                                                disabled={imageOptions.length === 0}
                                                title="Browse all cover images in a gallery"
                                                aria-label="Open cover image gallery"
                                                aria-haspopup="dialog"
                                                aria-expanded={coverGalleryOpen}
                                            >
                                                <Images
                                                    className="addblog-image-icon"
                                                    strokeWidth={2}
                                                    size={22}
                                                    aria-hidden
                                                />
                                                <span className="addblog-gallery-trigger__text">Gallery</span>
                                            </button>
                                        </div>

                                        <div className="hint">
                                            Uses the image key from src/assets/images (subfolders allowed).
                                        </div>
                                    </div>

                                    <div className="addblog-image-preview-box">
                                        {imageOptions.length > 0 && form.image && (
                                            <img
                                                className="addblog-image-preview-img"
                                                src={src}
                                                alt="Cover preview"
                                            />
                                        )}
                                    </div>
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="addblog-card addblog-publish-card">
                            <header className="abf-publish-head">
                                <div className="abf-publish-head-icon">
                                    <Sparkles size={20} strokeWidth={2} aria-hidden />
                                </div>
                                <div className="abf-publish-head-copy">
                                    <p className="abf-publish-eyebrow">Visibility</p>
                                    <h3 className="abf-publish-heading">Publication</h3>
                                </div>
                            </header>

                            <p className="abf-publish-lede">
                                Control where readers find this story. Live posts use{" "}
                                <code className="abf-inline-code">/blogs/your-slug</code>.
                            </p>

                            <div className="abf-publish-summary" aria-live="polite">
                                <span
                                    className={`abf-publish-pulse ${form.status === "published" ? "is-live" : ""}`}
                                    aria-hidden
                                />
                                <span className="abf-publish-summary-text">
                                    {form.status === "published"
                                        ? "Visible on the public blog"
                                        : "Visible only in this dashboard"}
                                </span>
                            </div>

                            <div className="abf-publish-grid" role="group" aria-label="Post visibility">
                                <button
                                    type="button"
                                    className={`abf-publish-tile ${form.status === "draft" ? "is-selected" : ""}`}
                                    onClick={() => setForm((p) => ({ ...p, status: "draft" }))}
                                    aria-pressed={form.status === "draft"}
                                >
                                    <span className="abf-publish-tile-glow" aria-hidden />
                                    {form.status === "draft" ? (
                                        <span className="abf-publish-tile-check" aria-hidden>
                                            <Check size={14} strokeWidth={3} />
                                        </span>
                                    ) : null}
                                    <span className="abf-publish-tile-icon abf-publish-tile-icon--draft">
                                        <FileEdit size={22} strokeWidth={2} aria-hidden />
                                    </span>
                                    <span className="abf-publish-tile-title">Draft</span>
                                    <span className="abf-publish-tile-desc">
                                        Keep editing privately. Not shown on the marketing site.
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className={`abf-publish-tile ${form.status === "published" ? "is-selected" : ""}`}
                                    onClick={() => setForm((p) => ({ ...p, status: "published" }))}
                                    aria-pressed={form.status === "published"}
                                >
                                    <span className="abf-publish-tile-glow abf-publish-tile-glow--live" aria-hidden />
                                    {form.status === "published" ? (
                                        <span className="abf-publish-tile-check abf-publish-tile-check--live" aria-hidden>
                                            <Check size={14} strokeWidth={3} />
                                        </span>
                                    ) : null}
                                    <span className="abf-publish-tile-icon abf-publish-tile-icon--live">
                                        <Globe2 size={22} strokeWidth={2} aria-hidden />
                                    </span>
                                    <span className="abf-publish-tile-title">Published</span>
                                    <span className="abf-publish-tile-desc">
                                        Appears in blog listings and at its public URL.
                                    </span>
                                </button>
                            </div>

                            <div className={`abf-publish-callout ${form.status === "published" ? "is-live" : "is-draft"}`}>
                                {form.status === "draft" ? (
                                    <Lock size={16} strokeWidth={2} className="abf-publish-callout-icon" aria-hidden />
                                ) : (
                                    <Rocket size={16} strokeWidth={2} className="abf-publish-callout-icon" aria-hidden />
                                )}
                                <p className="abf-publish-callout-text">
                                    {form.status === "draft"
                                        ? "Drafts stay in your admin library until you switch to Published and save."
                                        : "Readers can open this post from /blogs — ensure slug and content are final."}
                                </p>
                            </div>
                        </div>
                        </div>

                    </aside>
                </div>

                <div className="addblog-submit-container">
                    <Link to="/dashboard/blog-list" className="abf-cancel-link">Cancel</Link>
                    <button type="submit" disabled={!canSubmit}>
                        {primaryActionLabel}
                    </button>
                </div>
            </form>

            <CoverImageGalleryModal
                open={coverGalleryOpen}
                onClose={closeCoverGallery}
                imageKeys={imageOptions}
                selectedKey={form.image}
                onPick={handlePickCoverImage}
            />
        </section>
    );
};

export default BlogForm;
